import { differenceInCalendarDays, format, isThisYear } from "date-fns";
import type { GridTask, TaskStatus } from "./types";
import { deriveCellState } from "./helpers/cellState";

export const STATUS_LABEL: Record<TaskStatus, string> = {
  upcoming: "Not started",
  active: "Active",
  in_progress: "In progress",
  blocked: "Blocked",
  completed: "Complete",
  skipped: "Skipped",
  removed: "Removed",
  closed_stale: "Stale",
  failed: "Failed",
};

export function statusLabel(status: TaskStatus): string {
  return STATUS_LABEL[status] ?? status;
}

export function isClosed(status: TaskStatus): boolean {
  return ["completed", "skipped", "removed", "closed_stale"].includes(status);
}

/**
 * Match the relative-date voice used by features/Resources/Task/Components/DueDateTextWithColor
 * (Yesterday / Today / Tomorrow / "3 days ago" / "In 2 weeks") so the grid speaks the same
 * language as the rest of the task UI. We just use date-fns instead of moment.
 */
export function relativeDueLabel(date: Date, today: Date): string {
  const days = differenceInCalendarDays(date, today);

  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";

  if (days < -1) {
    const abs = Math.abs(days);
    if (abs < 7) return `${abs} days ago`;
    const weeks = Math.floor(abs / 7);
    if (weeks < 5) return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    const months = Math.floor(abs / 30);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  }

  if (days >= 2 && days <= 7) return `In ${days} days`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `In ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  const months = Math.floor(days / 30);
  return `In ${months} ${months === 1 ? "month" : "months"}`;
}

export function absoluteDueLabel(date: Date): string {
  return format(date, isThisYear(date) ? "MMM d" : "MMM d, yyyy");
}

/**
 * Pill text for a new hire's onboarding day:
 *   - future starts: "Today" / "Tomorrow" / "In X days"
 *   - past starts:   "Day 8" or "Day 90 · Month 3" once past the first month.
 *
 * `tone === "warm"` flags labels that should render in the orange/amber palette
 * (today, tomorrow, this-week) — matches how the rest of the task UI flags
 * "due soon" without escalating all the way to overdue red.
 */
export function dayIndicator(startDate: string | null, today: Date): {
  label: string;
  tone: "neutral" | "warm";
} {
  if (!startDate) return { label: "—", tone: "neutral" };
  const start = new Date(startDate);
  const days = differenceInCalendarDays(start, today);

  if (days === 0) return { label: "Today", tone: "warm" };
  if (days === 1) return { label: "Tomorrow", tone: "warm" };
  if (days >= 2 && days <= 7) return { label: `In ${days} days`, tone: "warm" };
  if (days > 7) return { label: `In ${days} days`, tone: "neutral" };

  const daysSince = Math.abs(days);
  // Active scope drops journeys past 1 year, but render a defensive flag for
  // anyone we somehow still hold on to.
  if (daysSince > 365) return { label: "Beyond program", tone: "neutral" };

  // Progressive bucketing for the secondary label:
  //   Days  1-7  → Week 1
  //   Days  8-14 → Week 2
  //   Days 15+   → Month N, counted in 30-day chunks starting after week 2
  // The "Day N" prefix stays so the precise day is always visible; the
  // bucket lives in a dim secondary tier that compact mode hides entirely.
  let bucket: string;
  if (daysSince <= 7) bucket = "Week 1";
  else if (daysSince <= 14) bucket = "Week 2";
  else bucket = `Month ${Math.ceil((daysSince - 14) / 30)}`;

  return { label: `Day ${daysSince} · ${bucket}`, tone: "neutral" };
}

export type QuickFilter = "all" | "open" | "overdue" | "mine" | "due_within_3_days";

// A task is "actionable" when the assignee can act on it right now — i.e. the
// cell state is one of open / due_soon / overdue. Locked not_started, done,
// skipped, closed_stale and na all fall out.
function isActionable(task: GridTask, today: Date): boolean {
  const kind = deriveCellState(task, today).kind;
  return kind === "open" || kind === "due_soon" || kind === "overdue";
}

export function matchesFilter(task: GridTask, filter: QuickFilter, currentUserId: number | null, today: Date): boolean {
  if (filter === "all") return true;
  // "Open" = the actionable queue — every cell the assignee could move on
  // right now. Includes overdue (still actionable), excludes locked-clock
  // not_started tasks and anything terminal.
  if (filter === "open") return isActionable(task, today);
  if (filter === "overdue") return deriveCellState(task, today).kind === "overdue";
  // "Mine" = my actionable tasks. Closed and locked tasks don't belong on the
  // focused list.
  if (filter === "mine") {
    return currentUserId != null
      && String(task.assigneeId) === String(currentUserId)
      && isActionable(task, today);
  }
  // Amber window (DUE_SOON_WINDOW_DAYS = 3). Overdue lives in its own chip so
  // the two buckets are disjoint and counts add up cleanly.
  if (filter === "due_within_3_days") {
    return deriveCellState(task, today).kind === "due_soon";
  }
  return true;
}
